"use client";

import * as React from "react";
import { motion } from "framer-motion";
import { Button } from "./ui/button";
import { ArrowLeft, FileText } from "lucide-react";
import { ScrollArea } from "./ui/scroll-area";
import openTurfLogo from '../assets/OpenTurf-logo.png';

interface TermsAndConditionsProps {
  onBack?: () => void;
}

export function TermsAndConditions({ onBack }: TermsAndConditionsProps) {
  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 via-white to-purple-50 flex flex-col relative overflow-hidden">
      {/* Background decorative elements */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute top-20 left-20 w-32 h-32 bg-purple-200/20 rounded-full blur-3xl" />
        <div className="absolute bottom-20 right-20 w-40 h-40 bg-purple-200/20 rounded-full blur-3xl" />
      </div>

      {/* Header */}
      <motion.header
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        className="relative z-10 p-6 border-b border-gray-200/50 bg-white/50 backdrop-blur-sm"
      >
        <div className="max-w-4xl mx-auto flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <Button
              variant="ghost"
              size="sm"
              onClick={onBack}
              className="text-purple-600 hover:text-purple-700 hover:bg-purple-50"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back
            </Button>
            <div className="flex items-center space-x-2">
              <FileText className="w-5 h-5 text-purple-600" />
              <span className="text-gray-900">Terms & Conditions</span>
            </div>
          </div>
          <img 
            src={openTurfLogo} 
            alt="OpenTurf Technologies" 
            className="h-8 w-auto"
          />
        </div>
      </motion.header>

      {/* Main content */}
      <div className="flex-1 p-6">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2, duration: 0.6 }}
          className="max-w-4xl mx-auto"
        >
          <div className="bg-white/80 backdrop-blur-sm rounded-3xl p-8 shadow-lg border border-white/20">
            <div className="text-center mb-8">
              <h1 className="text-gray-900 mb-2">Terms & Conditions</h1>
              <p className="text-gray-600">
                DayOne Candidate Onboarding Application
              </p>
              <p className="text-sm text-gray-500 mt-2">
                Last updated: {new Date().toLocaleDateString('en-US', { 
                  year: 'numeric', 
                  month: 'long', 
                  day: 'numeric' 
                })}
              </p>
            </div>

            <ScrollArea className="h-96">
              <div className="space-y-6 text-gray-700">
                <section>
                  <h3 className="text-gray-900 mb-3">1. Acceptance of Terms</h3>
                  <p>
                    By accessing and using the DayOne candidate onboarding application ("Service"), 
                    you accept and agree to be bound by the terms and provision of this agreement. 
                    This Service is provided by OpenTurf Technologies Pvt. Ltd. ("Company", "we", "us", or "our").
                  </p>
                </section>

                <section>
                  <h3 className="text-gray-900 mb-3">2. Purpose and Scope</h3>
                  <p>
                    DayOne is designed to facilitate the onboarding process for new employees and consultants. 
                    The Service allows you to:
                  </p>
                  <ul className="list-disc ml-6 mt-2 space-y-1">
                    <li>Verify your identity through OTP verification</li>
                    <li>Upload required documents for employment verification</li>
                    <li>Review and validate extracted information from uploaded documents</li>
                    <li>Complete mandatory onboarding forms and procedures</li>
                  </ul>
                </section>

                <section>
                  <h3 className="text-gray-900 mb-3">3. Data Collection and Processing</h3>
                  <p>
                    By using this Service, you consent to the collection and processing of your personal 
                    information as described in our Privacy Policy. We collect:
                  </p>
                  <ul className="list-disc ml-6 mt-2 space-y-1">
                    <li>Personal identification information (name, address, contact details)</li>
                    <li>Employment-related documents (ID proofs, educational certificates, etc.)</li>
                    <li>Biometric data where required for verification purposes</li>
                    <li>Communication records and interaction logs</li>
                  </ul>
                </section>

                <section>
                  <h3 className="text-gray-900 mb-3">4. AI-Powered Document Processing</h3>
                  <p>
                    Our Service utilizes TurfAI technology to extract and process information from 
                    uploaded documents. By using this Service, you acknowledge that:
                  </p>
                  <ul className="list-disc ml-6 mt-2 space-y-1">
                    <li>AI processing may not be 100% accurate and requires human verification</li>
                    <li>You are responsible for reviewing and confirming extracted information</li>
                    <li>We continuously improve our AI models using anonymized data</li>
                  </ul>
                </section>

                <section>
                  <h3 className="text-gray-900 mb-3">5. User Responsibilities</h3>
                  <p>You agree to:</p>
                  <ul className="list-disc ml-6 mt-2 space-y-1">
                    <li>Provide accurate and complete information</li>
                    <li>Upload only genuine and legally valid documents</li>
                    <li>Maintain the confidentiality of your account credentials</li>
                    <li>Report any suspicious activity or security concerns immediately</li>
                    <li>Complete the onboarding process in a timely manner</li>
                  </ul>
                </section>

                <section>
                  <h3 className="text-gray-900 mb-3">6. Data Security and Storage</h3>
                  <p>
                    We implement industry-standard security measures to protect your data. 
                    However, no method of transmission over the internet is 100% secure. 
                    We store your data in secure cloud infrastructure with encryption at rest and in transit.
                  </p>
                </section>

                <section>
                  <h3 className="text-gray-900 mb-3">7. Data Retention</h3>
                  <p>
                    We retain your personal information only as long as necessary for the purposes 
                    outlined in this agreement and as required by applicable laws and regulations. 
                    Upon completion of the onboarding process, certain data may be transferred to 
                    your employer's HR systems.
                  </p>
                </section>

                <section>
                  <h3 className="text-gray-900 mb-3">8. Limitation of Liability</h3>
                  <p>
                    To the fullest extent permitted by law, OpenTurf Technologies Pvt. Ltd. 
                    shall not be liable for any indirect, incidental, special, consequential, 
                    or punitive damages arising out of or relating to your use of the Service.
                  </p>
                </section>

                <section>
                  <h3 className="text-gray-900 mb-3">9. Governing Law</h3>
                  <p>
                    These terms shall be governed by and construed in accordance with the laws 
                    of India. Any disputes arising under these terms shall be subject to the 
                    exclusive jurisdiction of the courts in Bangalore, India.
                  </p>
                </section>

                <section>
                  <h3 className="text-gray-900 mb-3">10. Contact Information</h3>
                  <p>
                    If you have any questions about these Terms & Conditions, please contact us at:
                  </p>
                  <div className="mt-2 p-4 bg-gray-50 rounded-lg">
                    <p><strong>OpenTurf Technologies Pvt. Ltd.</strong></p>
                    <p>Email: support@openturf.com</p>
                    <p>Phone: +91-80-xxxx-xxxx</p>
                    <p>Address: Bangalore, Karnataka, India</p>
                  </div>
                </section>
              </div>
            </ScrollArea>
          </div>
        </motion.div>
      </div>

      {/* Footer */}
      <motion.footer
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.4, duration: 0.5 }}
        className="relative z-10 p-6 border-t border-gray-200/50 bg-white/50 backdrop-blur-sm"
      >
        <div className="max-w-4xl mx-auto flex items-center justify-between">
          <div className="flex items-center space-x-2">
            <img 
              src={openTurfLogo} 
              alt="OpenTurf Technologies" 
              className="h-6 w-auto opacity-60"
            />
            <span className="text-xs text-gray-500">
              © {new Date().getFullYear()} OpenTurf Technologies Pvt. Ltd.
            </span>
          </div>
          
          <div className="flex items-center space-x-4 text-sm text-gray-600">
            <span>Powered by TurfAI</span>
            <span className="w-1 h-1 bg-gray-400 rounded-full"></span>
            <span className="text-purple-600">Terms & Conditions</span>
          </div>
        </div>
      </motion.footer>
    </div>
  );
}